// Consolidated mock data for refund detail panels
// Follows the pattern of mockPaymentsPanels.ts for refund-specific detail views

import type { PaymentDetail, Receipt } from "@shared/components/panels/types/panel-common.types";
import type { EventData } from '@shared/types/events.types';
import type { BaseTimelineEvent } from '@shared/components/panels/components/domain-sections/Timeline/types';
import type { ActivityEvent } from '@shared/components/panels/components/domain-sections/Activity/types';

// ========================================
// Refund Details
// ========================================

export const REFUND_DETAILS: PaymentDetail[] = [
  { label: 'Refund Reference', value: 'REFREF8h93l2n4r0a5' },
  { label: 'Description', value: 'Refund for subscription cancellation' },
  { label: 'Original Payment', value: 'PAY-2024-09-28-001', isCopyable: true },
  { label: 'Trx Reference', value: 'TRX-2024-10-02-001' },
  { label: 'Execution Date', value: '02 Oct 2024 11:30' },
  { label: 'Card Network Reference', value: 'MC 928374651029384' },
  { label: 'ARN', value: '79875984375638474534' },
  { label: 'Authorization Code', value: '758394' },
  { label: 'Response Code', value: '00 (Approved)' },
  { label: 'Reason', value: 'Requested by customer' },
  { label: 'Refund Type', value: 'Full Refund' },
  { label: 'Order', value: 'ORD-00842' },
  { label: 'Subscription', value: 'SUBC-00847' },
  { label: 'Acquirer', value: 'Chase Bank' },
  { label: 'Organization', value: 'BrikLabs' },
  { label: 'Product Platform', value: 'Core Commerce' },
  { label: 'Processor', value: 'Stripe' },
  { label: 'Merchant', value: 'BRIK US' },
  { label: 'Source', value: 'Dashboard' },
];

export const REFUND_CUSTOMER_DETAILS: PaymentDetail[] = [
  { label: 'Account', value: 'user_9847abx52q' },
  { label: 'Name', value: 'John Doe' },
  { label: 'Email', value: 'johndoe@example.com' },
  { label: 'Billing address', value: '123 Main St, New York, NY 10001, United States' },
  { label: 'Risk', value: '4/50 (Low Risk)' },
  { label: 'Last activity', value: '02 Oct 2024 11:30' }
];

export const REFUND_FEES_DETAILS: PaymentDetail[] = [
  { label: 'Original payment amount', value: 'USD 1,680.00' },
  { label: 'Refund amount', value: 'USD 1,680.00' },
  { label: 'Refund fee', value: 'USD 0.00' },
  { label: 'Net refund', value: 'USD -1,680.00' },
  { label: 'Processing fee reversal', value: 'USD 28.70' },
  { label: 'Interchange reversal', value: 'USD 18.48' },
  { label: 'Scheme fee reversal', value: 'USD 3.46' },
  { label: 'Acquirer fee reversal', value: 'USD 11.76' },
  { label: 'Settlement adjustment', value: 'USD -1,651.30' },
  { label: 'Settlement currency (EUR)', value: '-1,519.20 (1USD = 0.92) EUR' },
];

export const REFUND_PAYMENT_METHOD_DETAILS: PaymentDetail[] = [
  { label: 'Payment method', value: 'Card' },
  { label: 'Card number', value: '•••• •••• •••• 0036' },
  { label: 'Card type', value: 'Mastercard' },
  { label: 'Expiry date', value: '12/2026' },
  { label: 'Cardholder name', value: 'John Doe' },
  { label: 'Billing address', value: '123 Main St, New York, NY 10001' },
  { label: 'Issuing bank', value: 'UniCredit Bulbank' },
  { label: 'Card country', value: 'Bulgaria' }
];

// ========================================
// Timeline Events
// ========================================

export function generateRefundTimelineEvents(): BaseTimelineEvent[] {
  return [
    {
      id: 'refund-initiated',
      title: 'Refund initiated',
      timestamp: '02 Oct 2024 11:30',
      details: [
        { action: 'Refund requested by agent via Dashboard', timestamp: '02 Oct 2024 11:30' },
        { action: 'Amount: BGN 80.00', timestamp: '02 Oct 2024 11:30' },
        { action: 'Reason: Requested by customer', timestamp: '02 Oct 2024 11:30' }
      ]
    },
    {
      id: 'refund-approved',
      title: 'Refund approved',
      timestamp: '02 Oct 2024 11:31',
      details: [
        { action: 'Auto-approved (within refund threshold)', timestamp: '02 Oct 2024 11:31' },
        { action: 'Approval policy: Standard', timestamp: '02 Oct 2024 11:31' }
      ]
    },
    {
      id: 'refund-processing',
      title: 'Refund processing',
      timestamp: '02 Oct 2024 11:32',
      details: [
        { action: 'Sent to processor (Stripe)', timestamp: '02 Oct 2024 11:32' },
        { action: 'Acquirer reference: ACQ-REF-98472', timestamp: '02 Oct 2024 11:32' }
      ]
    },
    {
      id: 'refund-completed',
      title: 'Refund completed',
      timestamp: '02 Oct 2024 11:35',
      details: [
        { action: 'Funds returned to cardholder', timestamp: '02 Oct 2024 11:35' },
        { action: 'Settlement batch: BATCH-2024-10-02', timestamp: '02 Oct 2024 11:35' },
        { action: 'Expected arrival: 5-10 business days', timestamp: '02 Oct 2024 11:35' }
      ]
    }
  ];
}

// ========================================
// Events Data
// ========================================

export const REFUND_EVENTS_DATA: EventData[] = [
  {
    id: 'evt_ref_001',
    eventType: 'refund.created',
    description: 'Refund re_1234567890 initiated for BGN 80.00 on payment 8a6e7a2d',
    timestamp: '02 Oct 2024 11:30',
    requestId: 'req_ref_abc123def456',
    status: 'succeeded'
  },
  {
    id: 'evt_ref_002',
    eventType: 'refund.approved',
    description: 'Refund auto-approved within standard threshold policy',
    timestamp: '02 Oct 2024 11:31',
    requestId: 'req_ref_def456ghi789',
    status: 'approved'
  },
  {
    id: 'evt_ref_003',
    eventType: 'refund.processing',
    description: 'Refund sent to processor for execution via Stripe',
    timestamp: '02 Oct 2024 11:32',
    requestId: 'req_ref_ghi789jkl012',
    status: 'processing'
  },
  {
    id: 'evt_ref_004',
    eventType: 'refund.succeeded',
    description: 'Refund of BGN 80.00 completed successfully. Funds returned to Mastercard ending 0036',
    timestamp: '02 Oct 2024 11:35',
    requestId: 'req_ref_jkl012mno345',
    status: 'succeeded'
  },
  {
    id: 'evt_ref_005',
    eventType: 'webhook.sent',
    description: 'Webhook notification delivered for refund.succeeded event',
    timestamp: '02 Oct 2024 11:35',
    requestId: 'req_ref_mno345pqr678',
    status: 'succeeded'
  },
  {
    id: 'evt_ref_006',
    eventType: 'settlement.adjusted',
    description: 'Settlement batch adjusted to reflect refund of BGN 80.00',
    timestamp: '02 Oct 2024 11:36',
    requestId: 'req_ref_pqr678stu901',
    status: 'succeeded'
  },
  {
    id: 'evt_ref_007',
    eventType: 'receipt.sent',
    description: 'Refund confirmation receipt emailed to johndoe@example.com',
    timestamp: '02 Oct 2024 11:36',
    requestId: 'req_ref_stu901vwx234',
    status: 'succeeded'
  },
  {
    id: 'evt_ref_008',
    eventType: 'refund.failed',
    description: 'Refund attempt failed - Card account closed. Retry scheduled for manual review',
    timestamp: '03 Oct 2024 09:15',
    requestId: 'req_ref_vwx234yza567',
    status: 'failed'
  },
  {
    id: 'evt_ref_009',
    eventType: 'refund.pending_review',
    description: 'Refund exceeds auto-approval threshold ($500). Queued for manual review by finance team',
    timestamp: '03 Oct 2024 10:00',
    requestId: 'req_ref_yza567bcd890',
    status: 'pending'
  },
  {
    id: 'evt_ref_010',
    eventType: 'refund.canceled',
    description: 'Refund canceled by operator before processing. Reason: Customer retracted request',
    timestamp: '03 Oct 2024 14:22',
    requestId: 'req_ref_bcd890efg123',
    status: 'failed'
  }
];

// ========================================
// Activity Events
// ========================================

export const REFUND_ACTIVITY_EVENTS: ActivityEvent[] = [
  {
    id: 'activity-refund-completed',
    type: 'refund_completed',
    title: 'Refund completed',
    description: 'Refund of BGN 80.00 processed successfully. Funds returned to Mastercard ending 0036.',
    timestamp: '2024-10-02T11:35:00Z',
    timestampDisplay: '02 Oct 2024 11:35',
    severity: 'info',
    requiresAction: false,
    icon: 'check-circle',
    statusCard: {
      title: 'Completed',
      timestampDisplay: '02 Oct 2024 11:35',
      description: 'Funds will appear on the customer\'s statement within 5-10 business days.',
    },
    subEvents: [
      { id: 'sub-refund-approved', title: 'Refund auto-approved', timestampDisplay: '02 Oct 2024 11:31' },
      { id: 'sub-refund-processed', title: 'Sent to processor', timestampDisplay: '02 Oct 2024 11:32' },
      { id: 'sub-refund-settled', title: 'Settlement adjusted', timestampDisplay: '02 Oct 2024 11:36' },
    ],
  },
  {
    id: 'activity-refund-initiated',
    type: 'refund_initiated',
    title: 'Refund requested',
    description: 'Customer requested a full refund for subscription cancellation.',
    timestamp: '2024-10-02T11:30:00Z',
    timestampDisplay: '02 Oct 2024 11:30',
    severity: 'info',
    requiresAction: false,
    icon: 'arrow-left',
    details: [
      { action: 'Refund initiated via Dashboard', timestamp: '02 Oct 2024 11:30' },
      { action: 'Reason: Requested by customer', timestamp: '02 Oct 2024 11:30' },
      { action: 'Amount: BGN 80.00 (Full refund)', timestamp: '02 Oct 2024 11:30' },
    ],
  },
];

// ========================================
// Receipts
// ========================================

export const REFUND_RECEIPTS: Receipt[] = [
  {
    id: 'receipt-refund-001',
    name: 'Refund confirmation receipt',
    timestamp: '02 Oct 2024 11:36'
  },
  {
    id: 'receipt-refund-002',
    name: 'Original payment receipt',
    timestamp: '28 Sep 2024 18:00'
  },
  {
    id: 'receipt-refund-003',
    name: 'Subscription cancellation notice',
    timestamp: '01 Oct 2024 09:15'
  }
];
